/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

export const GET_DASHBOARD_METRICS = `
  query GetDashboardMetrics {
    getAdminAnalytics {
      totalTrips
      activeDrivers
      totalUsers
      totalRevenue
      averageRating
      tripsByHour {
        hour
        trips
      }
      revenueByDay {
        day
        revenue
      }
      driverStatus {
        status
        count
        color
      }
      recentActivity {
        action
        time
      }
    }
  }
`;

export const GET_ACTIVE_TRIPS = `
  query GetActiveTrips {
    listAllTrips {
      items {
        id
        riderId
        riderName
        driverId
        driverName
        status
        pickup {
          address
          latitude
          longitude
        }
        destination {
          address
          latitude
          longitude
        }
        fareEstimate
        finalFare
        distance
        duration
        paymentMethod
        createdAt
        startedAt
        completedAt
        cancelledAt
        cancellationReason
      }
      nextToken
    }
  }
`;

export const GET_DRIVERS = `
  query GetDrivers($filter: AdminDriverFilter, $limit: Int, $nextToken: String) {
    listAllDrivers(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        phone
        email
        profilePhotoUrl
        status
        createdAt
      }
      nextToken
    }
  }
`;

export const GET_DRIVER_WITH_KYC = `
  query GetDriverWithKYC($id: ID!) {
    getAdminDriver(id: $id) {
      id
      name
      phone
      email
      profilePhotoUrl
      status
      createdAt
      updatedAt
    }
  }
`;

export const GET_KYC_APPLICATION = `
  query GetKycApplication($driverId: ID!) {
    getKycApplication(driverId: $driverId) {
      driverId
      applicationId
      status
      licenseNumber
      vehicleInfo {
        make
        model
        year
        color
        licensePlate
        vehicleType
      }
      documents {
        type
        s3Key
        viewUrl
        fileType
        fileSize
        status
        verified
        verifiedAt
        verifiedBy
        uploadedAt
      }
      licenseApproved
      vehicleApproved
      insuranceApproved
      dlFrontVerified
      dlBackVerified
      submittedAt
      reviewedAt
      reviewedBy
      rejectionReason
    }
  }
`;

export const GET_PENDING_DRIVERS = `
  query GetPendingDrivers($limit: Int, $nextToken: String) {
    listKYCApplications(filter: { status: "PENDING" }, limit: $limit, nextToken: $nextToken) {
      items {
        driverId
        applicationId
        status
        licenseNumber
        vehicleInfo {
          make
          model
          year
          color
          licensePlate
        }
        documents {
          type
          s3Key
          viewUrl
          fileType
          fileSize
          status
          verified
          uploadedAt
        }
        submittedAt
        reviewedAt
        reviewedBy
        rejectionReason
      }
      nextToken
    }
  }
`;

export const LIST_KYC_APPLICATIONS = `
  query ListKYCApplications($filter: KYCApplicationFilter, $limit: Int, $nextToken: String) {
    listKYCApplications(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        driverId
        applicationId
        status
        licenseNumber
        vehicleInfo {
          make
          model
          year
          color
          licensePlate
        }
        documents {
          type
          s3Key
          viewUrl
          fileType
          fileSize
          status
          verified
          uploadedAt
        }
        submittedAt
        reviewedAt
        reviewedBy
        rejectionReason
      }
      nextToken
    }
  }
`;

export const LIST_PENDING_KYC_APPLICATIONS = `
  query ListPendingKYCApplications($limit: Int, $nextToken: String) {
    listKYCApplications(filter: { status: "PENDING" }, limit: $limit, nextToken: $nextToken) {
      items {
        driverId
        applicationId
        status
        licenseNumber
        vehicleInfo {
          make
          model
          year
          color
          licensePlate
          vehicleType
        }
        documents {
          type
          s3Key
          viewUrl
          fileType
          fileSize
          status
          verified
          uploadedAt
          expiryDate
        }
        licenseApproved
        vehicleApproved
        insuranceApproved
        dlFrontVerified
        dlBackVerified
        licenseExpiryDate
        registrationExpiryDate
        insuranceExpiryDate
        submittedAt
        reviewedAt
        reviewedBy
        rejectionReason
      }
      nextToken
    }
  }
`;

export const UPDATE_DRIVER_KYC_STATUS = `
  mutation UpdateDriverKYCStatus($input: UpdateDriverKYCStatusInput!) {
    updateDriverKYCStatus(input: $input) {
      success
      message
      driver {
        id
        name
        status
      }
    }
  }
`;

export const VERIFY_KYC_DOCUMENT = `
  mutation VerifyKYCDocument($input: VerifyKYCDocumentInput!) {
    verifyKYCDocument(input: $input) {
      success
      message
      application {
        driverId
        licenseApproved
        vehicleApproved
        insuranceApproved
        dlFrontVerified
        dlBackVerified
        licenseExpiryDate
        registrationExpiryDate
        insuranceExpiryDate
        documents {
          type
          s3Key
          viewUrl
          verified
          verifiedAt
          verifiedBy
          expiryDate
        }
      }
    }
  }
`;

export const GET_TRIPS = `
  query GetTrips {
    listAllTrips {
      items {
        id
        riderId
        riderName
        driverId
        driverName
        status
        pickup {
          address
          latitude
          longitude
        }
        destination {
          address
          latitude
          longitude
        }
        fareEstimate
        finalFare
        distance
        duration
        paymentMethod
        createdAt
        startedAt
        completedAt
        cancelledAt
        cancellationReason
      }
      nextToken
    }
  }
`;

export const GET_ANALYTICS_DATA = `
  query GetAnalyticsData {
    getAdminAnalytics {
      totalTrips
      activeDrivers
      totalUsers
      totalRevenue
      averageRating
      tripsByHour {
        hour
        trips
      }
      revenueByDay {
        day
        revenue
      }
      driverStatus {
        status
        count
        color
      }
      recentActivity {
        action
        time
      }
    }
  }
`;

export const LIST_ADMIN_CASHOUT_REQUESTS = `
  query ListAdminCashoutRequests($status: CashoutStatus, $limit: Int, $nextToken: String) {
    listAdminCashoutRequests(status: $status, limit: $limit, nextToken: $nextToken) {
      items {
        requestId
        driverId
        amount
        fee
        netAmount
        currency
        payoutMethodType
        mobileMoneyNumber
        mobileMoneyProvider
        payoutType
        status
        requestedAt
        processedAt
        processedBy
        rejectionReason
        externalReference
        transactionId
        failureReason
      }
      nextToken
    }
  }
`;

export const LIST_DRIVER_POSITIONS = `
  query ListDriverPositions($centerLat: Float, $centerLng: Float, $radiusKm: Float) {
    listDriverPositions(centerLat: $centerLat, centerLng: $centerLng, radiusKm: $radiusKm) {
      items {
        driverId
        latitude
        longitude
        status
        vehicleType
        rating
        sampleTime
        distanceKm
      }
      total
    }
  }
`;

export const GET_SYSTEM_HEALTH = `
  query GetSystemHealth {
    getSystemHealth {
      queues {
        name
        depth
        inFlight
        isDLQ
        status
      }
      apiErrors {
        apiName
        errorCount4xx
        errorCount5xx
        windowMinutes
        status
      }
      errorLogVolume {
        todayCount
        yesterdayCount
        trend
      }
      operationalCounts {
        activeDrivers
        activeTrips
        pendingRequests
      }
      generatedAt
    }
  }
`;

export const CONFIRM_ADMIN_CASHOUT = `
  mutation ConfirmAdminCashout($requestId: ID!, $adminName: String!, $externalReference: String) {
    confirmAdminCashout(requestId: $requestId, adminName: $adminName, externalReference: $externalReference) {
      requestId
      status
      processedAt
      processedBy
      externalReference
      transactionId
    }
  }
`;

export const REJECT_ADMIN_CASHOUT = `
  mutation RejectAdminCashout($requestId: ID!, $adminName: String!, $reason: String!) {
    rejectAdminCashout(requestId: $requestId, adminName: $adminName, reason: $reason) {
      requestId
      status
      processedAt
      processedBy
      rejectionReason
    }
  }
`;

const DRIVER_BALANCE_FIELDS = `
  driverId
  netBalance
  availableForCashout
  amountOwed
  cardEarningsDue
  cashCommissionOwed
  commissionSettled
  cashCollected
  totalCashedOut
  lifetimeEarnings
  negativeSince
  updatedAt
`;

export const GET_DRIVER_BALANCE = `
  query GetDriverBalance($driverId: ID!) {
    getDriverBalance(driverId: $driverId) { ${DRIVER_BALANCE_FIELDS} }
  }
`;

export const RECORD_COMMISSION_PAYMENT = `
  mutation RecordCommissionPayment($driverId: ID!, $amount: Float!, $reference: String, $adminName: String!) {
    recordCommissionPayment(driverId: $driverId, amount: $amount, reference: $reference, adminName: $adminName) {
      ${DRIVER_BALANCE_FIELDS}
    }
  }
`;

export const RUN_BALANCE_ENFORCEMENT = `
  mutation RunBalanceEnforcement {
    runBalanceEnforcement {
      scanned
      negativeCount
      newlyDisabled
      reactivated
      ranAt
    }
  }
`;

export const SEND_DRIVER_NOTIFICATION = `
  mutation SendDriverNotification($input: SendDriverNotificationInput!) {
    sendDriverNotification(input: $input) {
      success
      message
      driverId
    }
  }
`;

export const LIST_PENDING_VEHICLES = `
  query ListPendingVehicles($limit: Int, $nextToken: String) {
    listPendingVehicles(limit: $limit, nextToken: $nextToken) {
      items {
        vehicleId
        driverId
        driverName
        make
        model
        year
        color
        licensePlate
        vehicleType
        status
        isDefault
        registrationUrl
        insuranceUrl
        createdAt
      }
      nextToken
    }
  }
`;

export const LIST_TEST_RUNS = `
  query ListTestRuns {
    listTestRuns {
      runId label createdAt updatedAt createdBy results
    }
  }
`;

export const CREATE_TEST_RUN = `
  mutation CreateTestRun($input: CreateTestRunInput!) {
    createTestRun(input: $input) {
      runId label createdAt updatedAt createdBy results
    }
  }
`;

export const UPDATE_TEST_RUN_RESULT = `
  mutation UpdateTestRunResult($input: UpdateTestRunResultInput!) {
    updateTestRunResult(input: $input) {
      runId updatedAt results
    }
  }
`;

export const DELETE_TEST_RUN = `
  mutation DeleteTestRun($runId: String!) {
    deleteTestRun(runId: $runId) {
      success message
    }
  }
`;
